import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function useCertificatePdf() {
  const [busy, setBusy] = useState(false);

  const fetchBlob = async (id) => {
    const res = await api.get(`/installations/${id}/pdf`, { responseType: "blob" });
    return res.data;
  };

  const download = async (id, certNumber) => {
    setBusy(true);
    try {
      const blob = await fetchBlob(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${certNumber || "certificate"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded");
    } catch (e) {
      toast.error("Failed to download certificate");
    } finally {
      setBusy(false);
    }
  };

  const print = async (id) => {
    setBusy(true);
    try {
      const blob = await fetchBlob(id);
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) {
        w.addEventListener("load", () => {
          try { w.print(); } catch (_) {}
        });
      }
      toast.success("Opening print view");
    } catch (e) {
      toast.error("Failed to open certificate");
    } finally {
      setBusy(false);
    }
  };

  const email = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/installations/${id}/email`);
      toast.success(data.message || "Certificate emailed");
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to email certificate");
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, download, print, email };
}

export function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    voided: "bg-[#E30613]/10 text-[#FF6069] border-[#E30613]/20",
    archived: "bg-white/5 text-neutral-400 border-white/10",
  };
  const label = { active: "Active", voided: "Voided", archived: "Archived" }[status] || status;
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${map[status] || "bg-white/5 text-neutral-400 border-white/10"}`}>
      {label}
    </span>
  );
}
